import { Card } from '@akademi/ui';
import { BlockRoot } from '../_shared/BlockRoot';
import { cardReset } from '../_shared/styles';

/**
 * Tenant-private partner-badge example block. Demonstrates the
 * registry-visibility pattern: ship the .tsx globally, gate the
 * registry row by tenant slug. Other tenants can't author with the
 * block, but their bundle still includes the small code surface.
 *
 * <p>Ports verbatim from ThemeMaker. Hosts that don't want this
 * block in their pickers simply omit the registry entry.
 */
export interface AcmePartnerBadgeProps {
  partnerName: string;
  tier?: 'bronze' | 'silver' | 'gold' | 'platinum';
  since?: string;
  /** Partner-program brand label. Default {@code Acme}. */
  brand?: string;
}

const TIER_COLORS = {
  bronze: { from: '#92400e', to: '#b45309', foreground: '#fef3c7', shadow: 'rgba(146,64,14,0.25)' },
  silver: { from: '#475569', to: '#64748b', foreground: '#f8fafc', shadow: 'rgba(71,85,105,0.25)' },
  gold: { from: '#b45309', to: '#f59e0b', foreground: '#fffbeb', shadow: 'rgba(180,83,9,0.30)' },
  platinum: {
    from: '#1e293b',
    to: '#334155',
    foreground: '#e2e8f0',
    shadow: 'rgba(30,41,59,0.35)',
  },
} as const;

export function AcmePartnerBadge({
  partnerName,
  tier = 'gold',
  since,
  brand = 'Acme',
}: AcmePartnerBadgeProps) {
  const c = TIER_COLORS[tier];
  return (
    <BlockRoot as="div" style={{ display: 'inline-flex', maxWidth: '100%' }}>
      <Card
        style={{
          ...cardReset,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 12,
          padding: 'var(--space-3, 12px) var(--space-5, 20px)',
          borderRadius: 'var(--radius-md, 8px)',
          background: `linear-gradient(135deg, ${c.from}, ${c.to})`,
          color: c.foreground,
          fontFamily: 'var(--font-family-display, inherit)',
        }}
      >
        <Shield />
        <div>
          <div
            style={{
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 'var(--font-tracking-widest, 0.1em)',
              opacity: 0.85,
            }}
          >
            {brand} · {tier} partner
          </div>
          <div style={{ fontSize: 'var(--font-size-lg, 18px)', fontWeight: 600, marginTop: 2 }}>
            {partnerName}
          </div>
          {since && (
            <div style={{ fontSize: '11px', marginTop: 2, opacity: 0.75 }}>
              Partner since {since}
            </div>
          )}
        </div>
      </Card>
    </BlockRoot>
  );
}

function Shield() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
      <path d="M20 4 L34 9 V20 Q34 30 20 36 Q6 30 6 20 V9 Z" fill="currentColor" opacity="0.18" />
      <path
        d="M14 19 L18 23 L26 15"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
